import { Link, Outlet, useLocation } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { PageHeader } from '@/components/ui/PageHeader';
import { ChevronRightIcon, RectangleGroupIcon, AdjustmentsHorizontalIcon, BellIcon, CommandLineIcon } from '@heroicons/react/24/outline';
import { usePageTitle } from '@/hooks/usePageTitle';

const settingsNavItems = [
    { label: 'Preferences', path: '/settings/preferences', icon: AdjustmentsHorizontalIcon, description: 'Configure display preferences and currency settings' },
    { label: 'Notifications', path: '/settings/notifications', icon: BellIcon, description: 'Manage email notification preferences' },
    { label: 'Attribution', path: '/settings/attribution', icon: RectangleGroupIcon, description: 'Manage collections and tags and assign your assets and debts' },
    { label: 'Connected apps', path: '/settings/connected-apps', icon: CommandLineIcon, description: 'Connect Claude, ChatGPT and other AI tools to your account' },
];

export function SettingsPage() {
    const location = useLocation();
    const isSettingsRoot = location.pathname === '/settings';

    return <Layout>{isSettingsRoot ? <SettingsIndex /> : <Outlet />}</Layout>;
}

/**
 * The settings landing page.
 * It owns the document title, because a parent effect runs after the child route
 * and would overwrite the title the child page sets.
 */
function SettingsIndex() {
    usePageTitle('Settings');

    return (
        <div className="space-y-6">
            <PageHeader title="Settings" />
            <div className="grid gap-4 md:grid-cols-2">
                {settingsNavItems.map((item) => {
                    const Icon = item.icon;
                    return (
                        <Link
                            key={item.path}
                            to={item.path}
                            className="group flex items-start gap-4 rounded-lg border border-gray-300 bg-white p-6 transition-all hover:border-accent hover:shadow-md dark:border-gray-700 dark:bg-secondary dark:hover:border-primary-light"
                        >
                            <div className="rounded-lg bg-gray-100 p-3 group-hover:bg-accent group-hover:text-white dark:bg-dark dark:group-hover:bg-primary-light">
                                <Icon className="h-6 w-6" />
                            </div>
                            <div className="flex-1">
                                <div className="flex items-center justify-between">
                                    <h3 className="font-semibold text-gray-900 dark:text-white">
                                        {item.label}
                                    </h3>
                                    <ChevronRightIcon className="h-5 w-5 text-gray-400 transition-transform group-hover:translate-x-1 dark:text-gray-500" />
                                </div>
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                    {item.description}
                                </p>
                            </div>
                        </Link>
                    );
                })}
            </div>
        </div>
    );
}
